import React, { useEffect, useRef, useState } from 'react';
import { Client, Message, Subscription } from 'stompjs';
import { useAppDispatch } from 'store/hooks';
import storage from 'utils/sessionStorage';
import {
  SOCKET_CONNECT_TIMEOUT_MS,
  SOCKET_HEALTH_CHECK_INTERVAL_MS,
  SOCKET_RECONNECT_DELAY_MS,
  disposeSocket,
  initSocket,
} from 'utils/socket';
import { checkSocketHealth, isSessionLive } from 'utils/socket-health';
import { buildTopicSubscriptions } from './socket-topic-subscriptions';

const getTopic = (topic: string) => {
  return `${topic}/${storage.shopId.get()}`;
};

type SocketContextProps = {
  socket: Client | null,
  connect: (shop_id: string) => void;
  unsubscribeAll: () => void;
};
export const SocketContext = React.createContext<SocketContextProps>({
  socket: null,
  connect: () => undefined,
  unsubscribeAll: () => undefined,
});

export const useSocketContext = () => React.useContext(SocketContext);

const useInitSocket = (): SocketContextProps => {
  const [socket, setSocket] = useState<Client | null>(null);
  const dispatch = useAppDispatch();
  // Client va shop hien tai phai nam trong ref: interval cung cac event listener
  // duoc dang ky mot lan duy nhat nen doc `socket` state se dong bang gia tri cua
  // lan render dau tien.
  const clientRef = useRef<Client | null>(null);
  const shopIdRef = useRef<string>('');
  // Registry destination -> subscription cua PHIEN hien tai. Day la nguon su that
  // de health check biet topic nao con dang duoc giu.
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());
  const reconnectTimerRef = useRef<number | null>(null);
  // Moc bat dau mot lan connect dang cho handshake.
  const connectingSinceRef = useRef<number | null>(null);
  const countingReconnect = useRef<number>(0);
  const waitingSubscribeArrays = useRef<{
    topic: string, listeningData: (message: Message) => void, disableDeviceId?: boolean;
  }[]>([]);

  /**
   * Subscribe idempotent theo destination.
   *
   * Goi lai lan hai se thay the subscription cu thay vi tao ban sao, neu khong
   * mot lan `connect` lap lai se lam handler chay hai lan cho moi message.
   */
  const subscribeTopic = (client: Client, destination: string, handler: (message: Message) => void) => {
    const current = subscriptionsRef.current.get(destination);
    if (current) {
      try {
        current.unsubscribe();
      } catch {
        // Transport da dong: subscription cu coi nhu da mat theo phien.
      }
    }
    subscriptionsRef.current.set(destination, client.subscribe(destination, handler));
  };

  const callBackReConnectSuccess = (_socket: Client, shopId: string) => {
    countingReconnect.current = 0;
    connectingSinceRef.current = null;
    if (!_socket) return;
    if (waitingSubscribeArrays.current.length) {
      waitingSubscribeArrays.current.forEach(waitItem => {
        subscribeTopic(_socket, getTopic(waitItem.topic), waitItem.listeningData);
      });
      waitingSubscribeArrays.current = [];
    }
    buildTopicSubscriptions(shopId, dispatch).forEach(({ destination, handler }) => {
      subscribeTopic(_socket, destination, handler);
    });
  };

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current == null) return;
    window.clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
  };

  /**
   * Vut bo client hien tai.
   *
   * Subscription cua mot phien da chet khong unsubscribe duoc nua nen chi can xoa
   * registry; `disposeSocket` lo phan dong transport.
   */
  const disposeClient = () => {
    const client = clientRef.current;
    subscriptionsRef.current.clear();
    clientRef.current = null;
    connectingSinceRef.current = null;
    setSocket(null);
    if (!client) return;
    disposeSocket(client);
  };

  const reconnect = (shopId: string) => {
    if (countingReconnect.current > 2) return;
    countingReconnect.current += 1;

    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      openSocket(shopId);
    }, SOCKET_RECONNECT_DELAY_MS);
  };

  /** Mo mot socket moi. Luon vut client cu truoc de khong de lai transport nua song. */
  const openSocket = (shopId: string) => {
    disposeClient();
    connectingSinceRef.current = Date.now();
    const _socket = initSocket();
    clientRef.current = _socket;
    setSocket(_socket);
    _socket.connect({}, () => callBackReConnectSuccess(_socket, shopId), () => {
      connectingSinceRef.current = null;
      reconnect(shopId);
    });
  };

  /**
   * Health check phat hien socket chet thi tu minh so huu viec khoi phuc: huy lich
   * retry dang cho, reset budget roi mo lai ngay.
   *
   * Phai reset budget vi `reconnect` bo cuoc vinh vien sau 3 lan that bai; giu
   * budget cu thi mot socket da tieu het luot se khong bao gio song lai duoc.
   */
  const forceReconnect = () => {
    const shopId = shopIdRef.current;
    if (!shopId) return;
    clearReconnectTimer();
    countingReconnect.current = 0;
    openSocket(shopId);
  };

  /** Kiem tra cac topic con connect khong, chet thi chu dong reconnect. */
  const runHealthCheck = () => {
    const shopId = shopIdRef.current;
    // Chua vao shop nao, hoac da roi shop: khong co gi phai giu song.
    if (!shopId) return;
    // Da co lich reconnect dang cho: de no chay, tranh mo hai socket chong nhau.
    if (reconnectTimerRef.current != null) return;
    // Dang bat tay thi chua connected la binh thuong, khong duoc pha ngang. Qua
    // han thi coi nhu treo va xu ly nhu mot socket chet.
    const connectingSince = connectingSinceRef.current;
    if (connectingSince != null && Date.now() - connectingSince < SOCKET_CONNECT_TIMEOUT_MS) return;

    const expectedTopics = buildTopicSubscriptions(shopId, dispatch).map(item => item.destination);
    const subscribedTopics = Array.from(subscriptionsRef.current.keys());
    if (checkSocketHealth(clientRef.current, expectedTopics, subscribedTopics).healthy) return;

    forceReconnect();
  };

  const connect = (shopId: string) => {
    shopIdRef.current = shopId;
    const current = clientRef.current;
    // Phien cu con song thi subscribe lai tren chinh no, khong mo socket thu hai.
    if (current && isSessionLive(current)) {
      callBackReConnectSuccess(current, shopId);
      return;
    }
    openSocket(shopId);
  };

  useEffect(() => {
    // Moi trang thai deu di qua ref va `dispatch` cua redux on dinh giua cac lan
    // render, nen dang ky mot lan la du va khong bao gio doc phai closure cu.
    const tick = () => runHealthCheck();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };
    const healthCheckId = window.setInterval(tick, SOCKET_HEALTH_CHECK_INTERVAL_MS);
    // May ngu day va rot mang la hai nguyen nhan lam chet socket pho bien nhat,
    // ca hai deu ban su kien nen khoi phuc duoc ngay thay vi cho het chu ky.
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', tick);

    return () => {
      window.clearInterval(healthCheckId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', tick);
      clearReconnectTimer();
      disposeClient();
    };
  }, []);

  const unsubscribeAll = () => {
    // Roi shop thi dung giu song nua, neu khong health check se tu subscribe lai.
    shopIdRef.current = '';
    subscriptionsRef.current.forEach(subscription => {
      try {
        subscription.unsubscribe();
      } catch {
        // Transport da dong, subscription coi nhu da mat theo phien.
      }
    });
    subscriptionsRef.current.clear();
  };

  return ({
    socket,
    connect,
    unsubscribeAll,
  });
};

export default useInitSocket;
